// frontend/src/components/community/ListBusinessModal.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  AlertCircle,
  CheckCircle,
  DollarSign,
  Store,
  ChevronRight,
  Loader,
  ArrowLeft,
  Plus,
  Star,
  Camera,
  Wallet,
  Info
} from 'lucide-react';
import marketplaceService from '../../services/marketplace.service';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import useWalletStore from '../../store/walletStore';
import { BUSINESS_CATEGORIES } from '../../config/categories';
import LocationPicker from '../common/LocationPicker';
import { toast } from 'react-hot-toast';

const ListBusinessModal = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { wallet, getWallet } = useWalletStore();
  const { 
    createBusiness, 
    getBusinessCategories, 
    businessCategories, 
    isLoading,
    checkCanCreateBusiness
  } = useMarketplaceStore();
  
  const [step, setStep] = useState(1);
  const [eligibility, setEligibility] = useState(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(true);
  const [tagInput, setTagInput] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    tags: [],
    location: '',
    address: '',
    city: '',
    state: '',
    phone: user?.phone || '',
    email: user?.email || '',
    website: '',
    logoUrl: null,
    coverImageUrl: null,
    images: [],
    socialLinks: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: ''
    }
  });

  // Check eligibility on mount
  useEffect(() => {
    checkEligibility();
    loadCategories();
    getWallet();
  }, []);

  // Auto-update location when city or state changes
  useEffect(() => {
    if (formData.city && formData.state) {
      setFormData(prev => ({
        ...prev,
        location: `${formData.city}, ${formData.state}`
      }));
    }
  }, [formData.city, formData.state]);

  const loadCategories = async () => {
    try {
      await getBusinessCategories();
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setCategoriesLoaded(true);
    }
  };

  const categories = businessCategories.length > 0 ? businessCategories : BUSINESS_CATEGORIES;

  const checkEligibility = async () => {
    setIsCheckingEligibility(true);
    try {
      const result = await checkCanCreateBusiness();
      setEligibility(result);
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      setEligibility({
        canCreate: false,
        reason: 'ERROR',
        message: error.response?.data?.error?.message || 'Unable to verify eligibility'
      });
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && formData.tags.length < 10) {
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (file, type = 'logo') => {
    setUploadingImages(true);
    try {
      const result = await marketplaceService.uploadImage(file);
      
      if (result.success) {
        if (type === 'logo') {
          setFormData(prev => ({ ...prev, logoUrl: result.data.url }));
        } else if (type === 'cover') {
          setFormData(prev => ({ ...prev, coverImageUrl: result.data.url }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            images: [...prev.images, result.data.url].slice(0, 5) 
          }));
        }
        
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Image upload error:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (stepNumber) => {
    const errors = {};
    
    switch (stepNumber) {
      case 1:
        if (!formData.name.trim()) errors.name = 'Business name is required';
        if (!formData.category) errors.category = 'Please select a category';
        if (!formData.description?.trim()) errors.description = 'Description is required';
        break;
        
      case 2:
        if (!formData.phone || !/^[0-9]{11}$/.test(formData.phone)) {
          errors.phone = 'Valid phone number is required';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          errors.email = 'Please enter a valid email';
        }
        if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
          errors.website = 'Please enter a valid website URL';
        }
        break;
        
      case 3:
        if (!formData.state) errors.state = 'Please select a state';
        if (!formData.city) errors.city = 'Please select a city/LGA';
        if (!formData.address?.trim()) errors.address = 'Address is required';
        break;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = marketplaceService.validateBusinessData(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      const formattedData = marketplaceService.formatBusinessData(formData);
      await createBusiness(formattedData);
      
      toast.success('Business listed successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Business creation error:', error);
    }
  };

  const goToWallet = () => {
    navigate('/wallet');
    onClose();
  };

  const progressPercentage = (step / 4) * 100;

  const renderCategorySelection = () => {
    if (!categoriesLoaded) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader className="h-5 w-5 text-primary-600 animate-spin" />
          <span className="ml-2 text-xs text-gray-500">Loading categories...</span>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-2">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => handleInputChange('category', category.id)}
            className={`p-2.5 rounded-xl text-left transition-all border ${
              formData.category === category.id
                ? 'bg-primary-50 border-primary-200 shadow-neomorph-inset'
                : 'bg-white border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
            }`}
          >
            <div className="flex items-start space-x-2">
              <span className="text-lg">{category.icon}</span>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-semibold text-gray-800">{category.name}</h4>
                <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">{category.description}</p>
              </div>
              {formData.category === category.id && (
                <CheckCircle className="h-3.5 w-3.5 text-primary-600 flex-shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  // Render eligibility check
  if (isCheckingEligibility) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
        <div className="bg-white rounded-xl shadow-neomorph max-w-sm w-full p-5 border border-primary-100/30">
          <div className="text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-primary-500/30">
              <Loader className="h-7 w-7 text-white animate-spin" />
            </div>
            <div className="flex items-center justify-center mb-1">
              <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
              <h3 className="text-base font-semibold text-gray-800">Checking Eligibility</h3>
            </div>
            <p className="text-xs text-gray-500">Verifying your wallet balance...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render not eligible - COMPACT VERSION
  if (!eligibility?.canCreate) {
    const isInsufficientBalance = eligibility?.reason === 'INSUFFICIENT_BALANCE';
    const isAlreadyHasBusiness = eligibility?.reason === 'ALREADY_HAS_BUSINESS';
    const shortfall = eligibility?.shortfall || 0;
    const walletBalance = eligibility?.walletBalance || 0;
    const requiredBalance = eligibility?.requiredBalance || 500;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
        <div className="bg-white rounded-xl shadow-neomorph max-w-sm w-full max-h-[95vh] overflow-y-auto border border-primary-100/30">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-sm font-semibold text-gray-800">List Your Business</h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1 shadow-neomorph-inset"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="text-center mb-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg ${
                isInsufficientBalance 
                  ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-yellow-500/30' 
                  : isAlreadyHasBusiness
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-500/30'
                    : 'bg-gradient-to-br from-orange-500 to-orange-600 shadow-orange-500/30'
              }`}>
                {isInsufficientBalance ? (
                  <Wallet className="h-7 w-7 text-white" />
                ) : isAlreadyHasBusiness ? (
                  <Store className="h-7 w-7 text-white" />
                ) : (
                  <AlertCircle className="h-7 w-7 text-white" />
                )}
              </div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1">
                {isInsufficientBalance ? 'Insufficient Wallet Balance' : 
                 isAlreadyHasBusiness ? 'Business Already Exists' : 
                 'Not Eligible'}
              </h4>
              <p className="text-xs text-gray-500">
                {eligibility?.message}
              </p>
            </div>

            {isInsufficientBalance && (
              <>
                {/* Compact explanation */}
                <div className="bg-blue-50 rounded-lg p-3 shadow-neomorph-inset border border-blue-200/50 mb-3">
                  <div className="flex items-start">
                    <div className="p-1 bg-blue-100 rounded-md shadow-neomorph-inset mr-2 flex-shrink-0">
                      <Info className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div>
                      <h5 className="text-[11px] font-medium text-blue-800 mb-0.5">Understanding the ₦500 Requirement</h5>
                      <p className="text-[10px] text-blue-700 leading-relaxed">
                        This is a <strong>minimum balance</strong> requirement, not a fee. 
                        The ₦500 stays in your wallet and is <strong>fully available</strong> for services.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Balance display - compact */}
                <div className="bg-yellow-50 rounded-lg p-3 shadow-neomorph-inset border border-yellow-200/50 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-600">Your Balance</span>
                    <span className="text-sm font-bold text-yellow-700">₦{walletBalance.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-gray-600">Minimum Required</span>
                    <span className="text-sm font-bold text-green-700">₦{requiredBalance.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-yellow-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (walletBalance / requiredBalance) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-yellow-700 mt-1.5">
                    Need ₦{shortfall.toLocaleString()} more
                  </p>
                </div>

                {/* Fund wallet - compact */}
                <div className="bg-green-50 rounded-lg p-3 shadow-neomorph-inset border border-green-200/50 mb-4">
                  <div className="flex items-start">
                    <div className="p-1 bg-green-100 rounded-md shadow-neomorph-inset mr-2 flex-shrink-0">
                      <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                    </div>
                    <div>
                      <h5 className="text-[11px] font-medium text-green-800 mb-1">Fund Your Wallet</h5>
                      <ul className="text-[10px] text-green-700 space-y-0.5 list-disc pl-4">
                        <li>Go to <strong>Wallet → Fund Wallet</strong></li>
                        <li>Choose payment method (Card, Bank Transfer, etc.)</li>
                        <li>Complete payment & return to list</li>
                      </ul>
                      <p className="text-[10px] text-green-700 mt-1 font-medium">
                        💡 Funds stay in your wallet for any Fastel service
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {isAlreadyHasBusiness && (
              <div className="bg-blue-50 rounded-lg p-3 shadow-neomorph-inset border border-blue-200/50 mb-4">
                <div className="flex items-start">
                  <div className="p-1 bg-blue-100 rounded-md shadow-neomorph-inset mr-2 flex-shrink-0">
                    <Store className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="text-[11px] font-medium text-blue-800 mb-0.5">You Already Have a Business</h5>
                    <p className="text-[10px] text-blue-700">
                      Edit your existing business instead. Go to <strong>"My Businesses"</strong> tab.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                onClick={onClose}
                className="w-full bg-white rounded-lg px-4 py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
              >
                Cancel
              </button>
              {isInsufficientBalance ? (
                <button
                  onClick={goToWallet}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white flex items-center justify-center"
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  Fund Wallet
                </button>
              ) : isAlreadyHasBusiness ? (
                <button
                  onClick={() => {
                    onClose();
                    window.location.href = '/marketplace?tab=mybusinesses';
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg px-4 py-2 shadow-md shadow-blue-500/30 hover:shadow-lg hover:shadow-blue-500/40 transition-all text-sm font-medium text-white flex items-center justify-center"
                >
                  <Store className="h-4 w-4 mr-2" />
                  Go to My Businesses
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="w-full bg-gradient-to-r from-gray-600 to-gray-700 rounded-lg px-4 py-2 shadow-md shadow-gray-500/30 hover:shadow-lg hover:shadow-gray-500/40 transition-all text-sm font-medium text-white"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render form steps (eligible user)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-neomorph max-w-3xl w-full my-4 sm:my-8 border border-primary-100/30">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center mb-1">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">List Your Business</h3>
              </div>
              <p className="text-xs text-gray-500 ml-3">
                Step {step} of 4 - {step === 1 ? 'Basic Info' : step === 2 ? 'Contact Details' : step === 3 ? 'Location & Media' : 'Review'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-[#E8ECF5] rounded-full h-2 shadow-neomorph-inset">
            <div 
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Eligibility badge */}
          <div className="mt-3 flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
            <span className="text-xs text-green-700">
              Eligible to list ✓ You have {eligibility?.availableSlots || 1} slot(s) available
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Tech Solutions NG"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.name ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                    maxLength={100}
                  />
                  {formErrors.name && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.name}</p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">Make it descriptive and memorable</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Category *
                  </label>
                  {renderCategorySelection()}
                  {formErrors.category && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe what your business does, services offered, unique selling points..."
                    rows={4}
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.description ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                    maxLength={1000}
                  />
                  {formErrors.description && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.description}</p>
                  )}
                  <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                    <span>Brief and clear description works best</span>
                    <span className="bg-[#E8ECF5] px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                      {formData.description.length}/1000
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Business Tags
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder="Add tags (e.g., web-design, seo)"
                      className="flex-1 px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      maxLength={20}
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      disabled={!tagInput.trim() || formData.tags.length >= 10}
                      className="bg-white rounded-lg px-3 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-700 border border-gray-100 disabled:opacity-50 flex-shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">Add up to 10 tags to help customers find you</p>
                  
                  {formData.tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg border border-primary-200 shadow-neomorph-inset"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1.5 text-primary-600 hover:text-primary-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Contact Information */}
            {step === 2 && (
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <Phone className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="08012345678"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.phone ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                  />
                  {formErrors.phone && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.phone}</p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">Primary contact number for your business</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <Mail className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="contact@business.com"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.email ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                  />
                  {formErrors.email && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.email}</p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">Optional - for business inquiries</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    <Globe className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourbusiness.com"
                    className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                      formErrors.website ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                    }`}
                  />
                  {formErrors.website && (
                    <p className="mt-1.5 text-xs text-red-600">{formErrors.website}</p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">Optional - include http:// or https://</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Social Media Links
                  </label>
                  <div className="space-y-3">
                    {[
                      { platform: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage', icon: '📘' },
                      { platform: 'twitter', label: 'Twitter', placeholder: 'https://twitter.com/yourhandle', icon: '🐦' },
                      { platform: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourprofile', icon: '📷' },
                      { platform: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/yourcompany', icon: '💼' }
                    ].map((social) => (
                      <div key={social.platform} className="flex items-center space-x-2">
                        <span className="w-8 text-center text-sm">{social.icon}</span>
                        <input
                          type="url"
                          value={formData.socialLinks[social.platform] || ''}
                          onChange={(e) => handleSocialLinkChange(social.platform, e.target.value)}
                          placeholder={social.placeholder}
                          className="flex-1 px-3 py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Location & Media */}
            {step === 3 && (
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-1 h-4 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
                    Location
                  </h4>

                  <div className="mb-4">
                    <LocationPicker
                      selectedState={formData.state}
                      selectedCity={formData.city}
                      onStateChange={(state) => handleInputChange('state', state)}
                      onCityChange={(city) => handleInputChange('city', city)}
                      stateError={formErrors.state}
                      cityError={formErrors.city}
                      label="Business Location"
                      required={true}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      <MapPin className="h-3.5 w-3.5 inline mr-1 text-gray-400" />
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="123 Business Street, Area"
                      className={`block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                        formErrors.address ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                      }`}
                    />
                    {formErrors.address && (
                      <p className="mt-1.5 text-xs text-red-600">{formErrors.address}</p>
                    )}
                    <p className="mt-1.5 text-xs text-gray-500">For customers to locate your business</p>
                  </div>
                </div>

                {/* Images Section */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-3">
                    Business Images
                  </label>
                  
                  {/* Logo Upload */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 mb-2">Logo</label>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                      {formData.logoUrl ? (
                        <div className="relative">
                          <img
                            src={formData.logoUrl}
                            alt="Business logo"
                            className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl object-cover shadow-neomorph-inset"
                          />
                          <button
                            type="button"
                            onClick={() => handleInputChange('logoUrl', null)}
                            className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                            {uploadingImages ? (
                              <Loader className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 animate-spin" />
                            ) : (
                              <>
                                <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                <span className="text-[10px] sm:text-xs text-gray-500 mt-1">Logo</span>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'logo');
                            }}
                            disabled={uploadingImages}
                          />
                        </label>
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Upload your business logo (Square, 200x200px recommended)</p>
                      </div>
                    </div>
                  </div>

                  {/* Cover Image Upload */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 mb-2">Cover Image</label>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                      {formData.coverImageUrl ? (
                        <div className="relative">
                          <img
                            src={formData.coverImageUrl}
                            alt="Business cover"
                            className="h-16 w-24 sm:h-20 sm:w-32 rounded-xl object-cover shadow-neomorph-inset"
                          />
                          <button
                            type="button"
                            onClick={() => handleInputChange('coverImageUrl', null)}
                            className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <div className="h-16 w-24 sm:h-20 sm:w-32 rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                            {uploadingImages ? (
                              <Loader className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 animate-spin" />
                            ) : (
                              <>
                                <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                <span className="text-[10px] sm:text-xs text-gray-500 mt-1">Cover</span>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'cover');
                            }}
                            disabled={uploadingImages}
                          />
                        </label>
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Upload a cover image (Banner, 1200x300px recommended)</p>
                      </div>
                    </div>
                  </div>

                  {/* Gallery Images */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">Gallery Images (Max 5)</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {formData.images.map((url, index) => (
                        <div key={index} className="relative">
                          <img
                            src={url}
                            alt={`Business image ${index + 1}`}
                            className="h-20 w-full rounded-xl object-cover shadow-neomorph-inset"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-lg p-1 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      
                      {formData.images.length < 5 && (
                        <label className="cursor-pointer">
                          <div className="h-20 w-full rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex flex-col items-center justify-center hover:shadow-neomorph transition-all border-2 border-dashed border-gray-300 hover:border-primary-400">
                            {uploadingImages ? (
                              <Loader className="h-5 w-5 text-primary-600 animate-spin" />
                            ) : (
                              <>
                                <Plus className="h-5 w-5 text-gray-400" />
                                <span className="text-[10px] text-gray-500 mt-1">Add</span>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'gallery');
                            }}
                            disabled={uploadingImages}
                          />
                        </label>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Showcase your business with photos of your products, services, or workspace
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <div className="space-y-4 sm:space-y-5">
                <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph-inset">
                  <h4 className="text-xs font-medium text-gray-600 mb-3">Review Your Business Listing</h4>
                  
                  <div className="space-y-4">
                    {/* Business Info */}
                    <div>
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Business Information</h5>
                      <div className="space-y-2 text-xs bg-white rounded-lg p-3 shadow-neomorph-inset">
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                          <span className="text-gray-500">Name:</span>
                          <span className="font-medium text-gray-800 break-words">{formData.name}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                          <span className="text-gray-500">Category:</span>
                          <span className="font-medium text-gray-800 break-words">
                            {categories.find(c => c.id === formData.category)?.name}
                          </span>
                        </div>
                        {formData.description && (
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                            <span className="text-gray-500">Description:</span>
                            <span className="font-medium text-gray-800 text-left sm:text-right max-w-xs break-words">
                              {formData.description.length > 100 
                                ? `${formData.description.substring(0, 100)}...` 
                                : formData.description}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div>
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Contact Information</h5>
                      <div className="space-y-2 text-xs bg-white rounded-lg p-3 shadow-neomorph-inset">
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                          <span className="text-gray-500">Phone:</span>
                          <span className="font-medium text-gray-800">{formData.phone}</span>
                        </div>
                        {formData.email && (
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                            <span className="text-gray-500">Email:</span>
                            <span className="font-medium text-gray-800 break-words">{formData.email}</span>
                          </div>
                        )}
                        {formData.website && (
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                            <span className="text-gray-500">Website:</span>
                            <span className="font-medium text-gray-800 break-words">{formData.website}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Location */}
                    <div>
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Location</h5>
                      <div className="space-y-2 text-xs bg-white rounded-lg p-3 shadow-neomorph-inset">
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                          <span className="text-gray-500">Address:</span>
                          <span className="font-medium text-gray-800 text-right break-words">{formData.address}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                          <span className="text-gray-500">City/State:</span>
                          <span className="font-medium text-gray-800">{formData.city}, {formData.state}</span>
                        </div>
                      </div>
                    </div>

                    {/* Eligibility Info */}
                    <div className="bg-green-50 rounded-xl p-3 shadow-neomorph-inset border border-green-200/50">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <h5 className="text-xs font-medium text-green-800 mb-1">Your Seller Tier</h5>
                          <div className="flex items-baseline flex-wrap gap-2">
                            <span className="text-base sm:text-lg font-bold text-green-700">{eligibility?.tier || 'BASIC'}</span>
                            <span className="text-xs text-green-600">(Available slots: {eligibility?.availableSlots || 1})</span>
                          </div>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg shadow-neomorph-inset w-fit">
                          <Star className="h-4 w-4 text-green-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-4 shadow-neomorph-inset border border-green-200/50">
                  <div className="flex items-start">
                    <div className="p-1.5 bg-green-100 rounded-md shadow-neomorph-inset mr-3 flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-green-800">Ready to List!</h4>
                      <p className="text-xs text-green-700 mt-1">
                        Your business will be reviewed and activated within 24 hours. You'll be notified when it's live.
                      </p>
                    </div>
                  </div>
                </div>

                {Object.keys(formErrors).length > 0 && (
                  <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
                    <div className="flex items-start">
                      <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-red-800">Please fix the following errors:</h4>
                        <ul className="text-xs text-red-700 mt-1 list-disc list-inside">
                          {Object.values(formErrors).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="p-4 sm:p-6 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-xl">
            <div className="flex justify-between gap-3">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  className="flex-1 sm:flex-none bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 flex items-center justify-center border border-gray-100"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 sm:flex-none bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
                >
                  Cancel
                </button>
              )}

              {step < 4 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="flex-1 sm:flex-none bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium flex items-center justify-center"
                >
                  Next Step
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 sm:flex-none bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium flex items-center justify-center disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Store className="h-4 w-4 mr-2" />
                      List My Business
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ListBusinessModal;